import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Select, Spin, message } from "antd";
import moment from "moment";
import { getConversations } from "../../../services/polly/classificationService";
import ConversationDrawer from "./ConversationDrawer";
import ResearchStatusBadge from "../ResearchQueue/ResearchStatusBadge";

const { Option } = Select;

function ConfidenceBadge({ confidence }) {
  let cls = 'ca-conv-badge';
  if (confidence >= 75) cls += ' high';
  else if (confidence >= 50) cls += ' med';
  else cls += ' low';
  return <span className={cls}>{confidence}%</span>;
}

export default function ConversationList({ topicId, flatTopics, period, onTopicUpdate }) {
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [confFilter, setConfFilter] = useState('all');

  const fetchConversations = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      const res = await getConversations(topicId, period);
      setConversations(res.conversations || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.warn("Failed to fetch conversations:", err);
      message.error("Failed to load conversations");
    }
    setLoading(false);
  }, [topicId, period]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const filtered = useMemo(() => {
    if (confFilter === 'all') return conversations;
    if (confFilter === 'high') return conversations.filter((c) => c.confidence >= 75);
    if (confFilter === 'med') return conversations.filter((c) => c.confidence >= 50 && c.confidence < 75);
    if (confFilter === 'low') return conversations.filter((c) => c.confidence < 50);
    return conversations;
  }, [conversations, confFilter]);

  const handleRowClick = (conv) => {
    setSelectedConversationId(conv.id);
    setDrawerVisible(true);
  };

  return (
    <>
      <div className="ca-conv-toolbar">
        <span className="ca-conv-subtitle">
          {total} conversation{total !== 1 ? 's' : ''} classified under this topic
        </span>
        <Select value={confFilter} onChange={setConfFilter} size="small" style={{ width: 180 }}>
          <Option value="all">All confidence levels</Option>
          <Option value="high">High only (&gt;75%)</Option>
          <Option value="med">Medium (50-75%)</Option>
          <Option value="low">Low confidence (&lt;50%)</Option>
        </Select>
      </div>

      <Spin spinning={loading}>
        {filtered.length === 0 && !loading ? (
          <div className="conversation-empty">
            No conversations {confFilter !== 'all' ? 'matching this filter' : 'classified under this topic yet'}.
          </div>
        ) : (
          filtered.map((conv) => (
            <div key={conv.id} className="ca-conv-row" onClick={() => handleRowClick(conv)}>
              <div className="ca-conv-top">
                <div className="ca-conv-text">
                  "{conv.preview}"
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ResearchStatusBadge researchStatus={conv.researchStatus} />
                  <ConfidenceBadge confidence={conv.confidence} />
                </div>
              </div>
              <div className="ca-conv-meta">
                {conv.classifiedAt ? moment(conv.classifiedAt).fromNow() : ''}
                {conv.messageCount > 0 && <> &middot; {conv.messageCount} message{conv.messageCount !== 1 ? 's' : ''}</>}
                {conv.contactName && <> &middot; {conv.contactName}</>}
              </div>
            </div>
          ))
        )}
      </Spin>

      <ConversationDrawer
        visible={drawerVisible}
        conversationId={selectedConversationId}
        flatTopics={flatTopics}
        onClose={() => setDrawerVisible(false)}
        onReassignSuccess={() => { fetchConversations(); onTopicUpdate && onTopicUpdate(); }}
      />
    </>
  );
}
